import {
  DataGridPro,
  DataGridProProps,
  GridCallbackDetails,
  GridColDef,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridInitialState,
  GridPaginationModel,
  GridSortModel,
  GridToolbar,
  GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useEffect, useState, useCallback } from "react";
import {
  getVisibleColumnsFromLocalStorage,
  saveVisibleColumnsToLocalStorage,
} from "../../../helpers/columnVisibility";
import {
  getColumnOrderFromLocalStorage,
  saveColumnOrderToLocalStorage,
} from "../../../helpers/columnOrder";
import { saveRowsPerPageToLocalStorage } from "../../../helpers/rowsPerPage";
import ReactDOM from "react-dom";
import GridPagination, {
  GridPaginationProps,
} from "../../pagination/GridPagination";
import { GridPaginationChangePageAction } from "../../pagination/PaginationActions";
import { debounce } from "lodash";
import { RowSelectorMode } from "../../../types/datatable/RowSelectorMode";
import TableCell from "./TableCell";
import "./DataTableStyles.css";
import { getColumnWidthsFromLocalStorage, saveColumnWidthToLocalStorage } from "../../../helpers/columnWidth";

declare module "@mui/x-data-grid" {
  interface PaginationPropsOverrides extends GridPaginationProps {}
}

export type DataTableColDef<
  TRowModel extends GridValidRowModel = any,
  V = any,
  F = V,
> = GridColDef<TRowModel, V, F> & {
  showMenuOnHover?: boolean;
};

export interface DataTableProps extends Omit<
  DataGridProProps,
  "onSortModelChange" | "columns"
> {
  columns: DataTableColDef[];
  tableId?: string;
  initialStateValue?: GridInitialState;
  approximate?: boolean;
  highlightRowId?: string;
  startApproximatingAt?: number;
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => Promise<GridSortModel> | GridSortModel;
  rowSelectorMode?: RowSelectorMode; // this is used to clear out the checkbox in the header row if it's not needed
  onNextPage?: GridPaginationChangePageAction;
  onPreviousPage?: GridPaginationChangePageAction;
  refresh?: () => void | Promise<void>;
  pageIndex?: number;
  pageSizeOptions?: readonly (number | { value: number; label: string })[];
  onPageSizeChange?: (pageSize: number) => void;
}

const AUTOSIZE_OPTIONS = {
  expand: false,
  includeHeaders: true,
  includeOutliers: true,
};

const DataTable = ({
  tableId,
  rows,
  refresh,
  onPaginationModelChange,
  paginationModel,
  onSortModelChange,
  highlightRowId = "",
  slots = {},
  slotProps = {},
  loading,
  approximate = false,
  startApproximatingAt = 0,
  paginationMode = "client",
  sortingMode = "client",
  onNextPage,
  onPreviousPage,
  rowSelectorMode = RowSelectorMode.SINGLE,
  pageIndex,
  pageSizeOptions,
  columns,
  onPageSizeChange,
  ...props
}: DataTableProps) => {
  const newApiRef = useGridApiRef();
  const apiRef = props.apiRef ?? newApiRef;

  const [sortModel, setSortModel] = useState<GridSortModel>(
    props.initialState?.sorting?.sortModel ?? [],
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    props.columnVisibilityModel,
  );
  const [orderedFields, setOrderedFields] = useState<string[] | undefined>(
    undefined,
  );

  const [columnWidths, setColumnWidths] = useState<
    Record<string, number> | undefined
  >(undefined);

  const handleRefresh = useCallback(() => {
    if (refresh) {
      refresh();
    }
  }, [refresh]);

  const [hasAutoSized, setHasAutoSized] = useState(false);

  const autosizeColumns = useCallback(() => {
    ReactDOM.flushSync(() => {
      apiRef.current.autosizeColumns(AUTOSIZE_OPTIONS);
    });
    setHasAutoSized(true);

    handleRefresh();
  }, [apiRef, handleRefresh]);

  useEffect(() => {
    const debouncedAutosize = debounce(autosizeColumns, 500);

    if (!loading && rows && rows.length > 0 && !hasAutoSized && !columnWidths) {
      debouncedAutosize();
    }

    return () => {
      debouncedAutosize.cancel();
    };
  }, [loading, rows, hasAutoSized, autosizeColumns, columnWidths]);

  useEffect(() => {
    setColumnVisibilityModel(
      getVisibleColumnsFromLocalStorage(tableId, props.initialState),
    );
  }, [tableId, props.initialState]);

  useEffect(() => {
    const savedOrder = getColumnOrderFromLocalStorage(tableId);
    if (savedOrder) {
      setOrderedFields(savedOrder);
    }
  }, [tableId]);

  useEffect(() => {
    const savedWidths = getColumnWidthsFromLocalStorage(tableId);
    if (savedWidths) setColumnWidths(savedWidths);
  }, [tableId]);

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model);
    saveVisibleColumnsToLocalStorage(tableId, model);
  };

  const onColumnOrderChange = () => {
    const allColumns = apiRef.current.getAllColumns();
    const newOrderedFields = allColumns.map((col) => col.field);
    setOrderedFields(newOrderedFields);
    saveColumnOrderToLocalStorage(tableId, newOrderedFields);
  };

  const onColumnWidthChange = ({ colDef, width }: GridColumnResizeParams) => {
    setColumnWidths((prev) => ({ ...prev, [colDef.field]: width }));
    saveColumnWidthToLocalStorage(tableId, colDef.field, width);
  };

  const orderedColumns = useCallback(() => {
    if (!orderedFields || orderedFields.length === 0) {
      return columns.map((col) => ({
        ...col,
        width: columnWidths?.[col.field] ?? col.width
      }));
    }

    const columnsByField = new Map(columns.map((col) => [col.field, col]));
    const reordered: DataTableColDef[] = [];
    const allFields = new Set(columns.map((col) => col.field));

    for (const field of orderedFields) {
      const column = columnsByField.get(field);
      if (column) {
        reordered.push(column);
        allFields.delete(field);
      }
    }

    for (const column of columns) {
      if (allFields.has(column.field)) {
        reordered.push(column);
      }
    }

    return reordered.map((col) => ({
      ...col,
      width: columnWidths?.[col.field] ?? col.width
    }));

  }, [columns, orderedFields, columnWidths]);

  return (
    <div style={{ height: "97%", display: "flex", flexDirection: "column" }}>
      <DataGridPro
        pagination
        {...props}
        columns={columnsRenderedAsTableCells(orderedColumns())}
        slots={{
          toolbar: GridToolbar,
          pagination: GridPagination,
          ...slots,
        }}
        slotProps={{
          ...slotProps,
          pagination: {
            pageIndex,
            approximate,
            startApproximatingAt,
            estimatedRowCount: props.estimatedRowCount,
            rowCount: props.rowCount,
            onNextPage,
            onPreviousPage,
            pageSizeOptions,
            pageSize: paginationModel?.pageSize,
            onPageSizeChange,
          },
        }}
        rows={rows}
        editMode="row"
        checkboxSelection={rowSelectorMode === RowSelectorMode.MULTIPLE}
        disableRowSelectionOnClick
        paginationMode={paginationMode}
        sortingMode={sortingMode}
        paginationModel={paginationModel}
        onPaginationModelChange={(
          model: GridPaginationModel,
          details: GridCallbackDetails,
        ) => {
          saveRowsPerPageToLocalStorage(model.pageSize, tableId);
          onPaginationModelChange?.(model, details);
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onColumnOrderChange={onColumnOrderChange}
        onColumnWidthChange={onColumnWidthChange}
        initialState={{
          ...props.initialState,
          pagination: {
            paginationModel,
            ...props.initialState?.pagination,
          },
        }}
        sortModel={sortModel}
        onSortModelChange={async (
          model: GridSortModel,
          details: GridCallbackDetails,
        ) => {
          const newSortModel = await onSortModelChange?.(model, details);
          setSortModel(newSortModel ?? model);
        }}
        pageSizeOptions={pageSizeOptions}
        autosizeOptions={AUTOSIZE_OPTIONS}
        loading={loading}
        apiRef={apiRef}
        getRowClassName={(params) =>
          highlightRowId && params.id === highlightRowId
            ? "highlighted-row"
            : ""
        }
      />
    </div>
  );
};

function columnsRenderedAsTableCells(
  columns: readonly GridColDef[],
): GridColDef[] {
  return columns.map((column) => {
    if (column.type === "actions") {
      return column;
    }
    return {
      ...column,
      renderCell: (params) => (
        <TableCell {...params} renderCell={column.renderCell} />
      ),
    };
  });
}

export default DataTable;
